import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Observable, Subject, Subscriber, TimeoutError } from 'rxjs';
import { FormBuilder } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { FormService } from '../../../../services/base/form.service';
import { AuthenticationService } from '../../../../services/base/authentication.service';
import { debounceTime, distinctUntilChanged, take, takeUntil, timeout } from 'rxjs/operators';
import { FiltersService } from '../../../../services/base/filters.service';
import { ApiService } from '../../../../services/base/api.service';
import { FilterValue } from '../../../../interfaces/base/filter-value';
import { Filters } from '../../../../interfaces/base/filters';
import { Source } from '../../../../interfaces/routering/source';
import { Tile } from '../../../../interfaces/routering/tile';
import { Action } from '../../../../interfaces/routering/action';
import { Current } from '../../../../interfaces/configuration/current';
import { StandardReaction } from '../../../../interfaces/configuration/standard-reaction';
import { MtxDatetimepickerInputEvent } from '@ng-matero/extensions/datetimepicker';
import * as moment from 'moment/moment';
import { SearchAddressResults } from '../../../../interfaces/base/search-address-results';
import { NationaalGeoregisterSuggest } from '../../../../interfaces/external-services/nationaal-georegister-suggest';
import { Organisation } from '../../../../interfaces/routering/organisation';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { NationaalGeoregisterLookup } from '../../../../interfaces/external-services/nationaal-georegister-lookup';
import { TaskService } from '../../../../services/tasks/task.service';

@Component({
  selector: 'fury-tasks-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
})

export class ReportsFiltersComponent implements OnInit, AfterViewInit, OnDestroy {

  public filters: Filters = {
    sources: {
      preload: true,
      isLoading: false,
      data: <Source[]>[]
    },
    tiles: {
      preload: true,
      isLoading: false,
      data: <Tile[]>[]
    },
    actions: {
      preload: true,
      isLoading: false,
      data: <Action[]>[]
    },
    currents: {
      preload: true,
      isLoading: false,
      data: <Current[]>[]
    },
    impactRegistrations: {
      preload: true,
      isLoading: false,
      data: <Source[]>[]
    },
    standardReactions: {
      preload: false,
      isLoading: false,
      subscription: null,
      data: <StandardReaction[]>[]
    },
  };

  @Input() pageType: 'open' | 'closed' | 'all' = 'open';
  @Input() clearFilters: EventEmitter<any> = new EventEmitter<any>();
  @Output() closed: EventEmitter<any> = new EventEmitter();
  @Output() updateFilters: EventEmitter<any> = new EventEmitter<any>();
  @Output() triggerFiltering: EventEmitter<any> = new EventEmitter<any>();

  public form = this.fb.group({
    created_at_start: <string>null,
    created_at_end: <string>null,
    closed_at_start: <string>null,
    closed_at_end: <string>null,
    tile_id: <number>null,
    address: <string>null,
    streetname: <string>null,
    house_number: <string>null,
    house_number_addition: <string>null,
    postalcode: <string>null,
    city: <string>null,
    source_id: <number>null,
    action_id: <number>null,
    current_id: <number>null,
    impact_registration_id: <number>null,
    outside_number: <string>null,
  });

  public searchTaskLocationsTimedOut: boolean = false;
  public searchTaskLocationsNoResults: boolean = false;
  public searchTaskLocationsIsLoading: boolean = false;
  public searchTaskLocations: SearchAddressResults[] = [];
  public coordinatesAreOutsideTile: boolean | Tile = null;

  private destroy$: Subject<null> = new Subject<null>();

  constructor(private fb: FormBuilder,
              private filtersService: FiltersService,
              private cd: ChangeDetectorRef,
              private api: ApiService,
              private taskService: TaskService,
              private http: HttpClient,
              public formService: FormService,
              public authenticationService: AuthenticationService,
  ) {
  }

  ngOnInit(): void {
    this.formService.form = this.form;

    Object.keys(this.form.controls).map((key: string): void => {
      if (typeof this.api.routeParams.filters[key] !== 'undefined') {
        this.form.controls[key].setValue(this.api.routeParams.filters[key]);
      }
    });

    this.filtersService.filters = this.filters;

    Object.keys(this.filters).map((key: string): void => {
      if (this.filters[key].preload) {
        this.filters[key].isLoading = true;

        this.filtersService[key]().pipe(takeUntil(this.destroy$)).subscribe((data: any): void => {
          this.filters[key].data = data;
          this.filters[key].isLoading = false;
        });
      }
    });

    this.form.controls.address.valueChanges.pipe(
      debounceTime(250),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe((value: string | any): void => {
      this.searchTaskLocationsNoResults = false;
      this.searchTaskLocationsTimedOut = false;

      if (value) {
        this.searchTaskLocationsIsLoading = true;

        if (typeof value === 'string') {
          this.getSearchedAddress(value).subscribe((response: NationaalGeoregisterSuggest): void => {
            if (response) {
              if (response.response.numFound > 0) {
                this.searchTaskLocations = response.response.docs;
              } else {
                this.searchTaskLocationsNoResults = true;
              }
            }

            this.searchTaskLocationsIsLoading = false;
            this.cd.detectChanges();
          });
        } else {
          this.selectTaskAddress(value.id);
        }
      }
      this.cd.detectChanges();
    });

    this.clearFilters.subscribe((): void => {
      this.form.reset();
      this.form.updateValueAndValidity();
    });
  }

  ngAfterViewInit(): void {
    this.api.preSetFilters.pipe(
      take(1),
      takeUntil(this.destroy$)
    ).subscribe((values: FilterValue[]): void => {
      values.forEach((value: FilterValue): void => {
        if (typeof this.form.controls[value.name] !== 'undefined') {
          this.form.controls[value.name].setValue(value.value, {emitEvent: false});
        }
      });
    });

    this.cd.detectChanges();
  }

  getTooltipText(e: MatFormField): string | null {
    const label: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label') as HTMLLabelElement;
    const matLabel: HTMLLabelElement = e._elementRef.nativeElement.querySelector('.mat-form-field-label mat-label') as HTMLLabelElement;

    if (matLabel) {
      const children: HTMLCollection = matLabel.children;
      for (let i: number = 0; i < children.length; i++) {
        if (children[i].tagName === 'SPAN') {
          return label.scrollWidth > label.clientWidth ? children[i].innerHTML : null;
        }
      }
    }

    return null;
  }

  getSearchedAddress(value: string): Observable<NationaalGeoregisterSuggest> {
    return new Observable<NationaalGeoregisterSuggest>((o: Subscriber<NationaalGeoregisterSuggest>): void => {
      this.clearAddressSuggestions();
      this.authenticationService.organisation$.pipe(takeUntil(this.destroy$)).subscribe((organisation: Organisation): void => {
        const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
          '?q=' + encodeURIComponent(value) +
          '&rows=10' +
          '&bq=' + encodeURIComponent('type:provincie^0.5') +
          '&bq=' + encodeURIComponent('type:gemeente^0.5') +
          '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
          '&bq=' + encodeURIComponent('type:weg^0.5') +
          '&bq=' + encodeURIComponent('type:adres^1.5') +
          '&fq=bron:"BAG"' +
          (organisation ?
            '&fq=gemeentenaam:(' + organisation.center_municipality + ')'
            : '');

        this.http.get(url, {
          observe: 'response',
          responseType: 'json'
        }).pipe(
          takeUntil(this.destroy$),
          timeout(5000)
        ).subscribe(
          (response: HttpResponse<NationaalGeoregisterSuggest>): void => {
            o.next(response.body);
          },
          (error): void => {
            if (error instanceof TimeoutError) {
              this.searchTaskLocationsTimedOut = true;
            }
            o.next(undefined);
          });
      });
    });
  }

  selectTaskAddress(id: string): void {
    this.searchTaskLocations = [];
    this.searchTaskLocationsIsLoading = true;
    this.cd.detectChanges();

    const url: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
      '?id=' + encodeURIComponent(id);

    this.http.get(url, {
      observe: 'response',
      responseType: 'json'
    }).pipe(
      takeUntil(this.destroy$),
      timeout(5000)
    ).subscribe(
      (response: HttpResponse<NationaalGeoregisterLookup>): void => {
        if (response.body.response.numFound > 0) {
          const data = response.body.response.docs[0],
            dataLatLng: string[] = data.centroide_ll.replace('POINT(', '').replace(')', '').split(' ');

          if (this.form.controls.tile_id.value) {
            const lat: number = parseFloat(dataLatLng[0]),
              lng: number = parseFloat(dataLatLng[1]);
            if (lat && lng) {
              if (
                !this.taskService.checkIfCoordsAreInsideTheBox(
                  this.filters.tiles.data.find((tile: Tile): boolean => tile.id === this.form.controls.tile_id.value),
                  [
                    lat,
                    lng
                  ]
                )
              ) {
                const tileFound: Tile = this.filters.tiles.data.find((tile: Tile): boolean =>
                  lat >= tile.bottomright_lat && lat <= tile.topleft_lat &&
                  lng >= tile.topleft_lng && lng <= tile.bottomright_lng
                );
                this.coordinatesAreOutsideTile = tileFound ?? true;
              }
            }
          }

          this.form.controls.address.setValue(
            data.straatnaam + (data.huis_nlt ? ' ' + data.huis_nlt : '') + ', ' + data.woonplaatsnaam
            , {emitEvent: false});
          this.form.controls.streetname.setValue(data.straatnaam ?? null, {emitEvent: false});
          this.form.controls.house_number.setValue((typeof data.huisnummer !== 'undefined' ? data.huisnummer : null), {emitEvent: false});
          this.form.controls.house_number_addition.setValue((typeof data.huisletter !== 'undefined' ? data.huisletter : null), {emitEvent: false});
          this.form.controls.postalcode.setValue(data.postcode ?? null, {emitEvent: false});
          this.form.controls.city.setValue(data.woonplaatsnaam ?? null, {emitEvent: false});

          if (typeof data.postcode === 'undefined') {
            const url2: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/suggest' +
              '?q=' + encodeURIComponent(data.straatnaam + ' ' + (data.huisnummer ?? 1) + ' ' + data.woonplaatsnaam) +
              '&rows=1' +
              '&bq=' + encodeURIComponent('type:provincie^0.5') +
              '&bq=' + encodeURIComponent('type:gemeente^0.5') +
              '&bq=' + encodeURIComponent('type:woonplaats^0.5') +
              '&bq=' + encodeURIComponent('type:weg^0.5') +
              '&bq=' + encodeURIComponent('type:adres^1.5') +
              '&fq=(bron:"BAG") AND type:(adres AND postcode:*)'
            ;

            this.http.get(url2, {
              observe: 'response',
              responseType: 'json'
            }).pipe(
              takeUntil(this.destroy$),
              timeout(5000)
            ).subscribe(
              (response2: HttpResponse<NationaalGeoregisterSuggest>): void => {
                if (response2.body.response.numFound > 0) {

                  const url3: string = 'https://api.pdok.nl/bzk/locatieserver/search/v3_1/lookup' +
                    '?id=' + encodeURIComponent(response2.body.response.docs[0].id);

                  this.http.get(url3, {
                    observe: 'response',
                    responseType: 'json'
                  }).pipe(
                    takeUntil(this.destroy$),
                    timeout(5000)
                  ).subscribe(
                    (response3: HttpResponse<NationaalGeoregisterLookup>): void => {
                      if (response3.body.response.numFound > 0) {
                        const data2 = response3.body.response.docs[0];

                        this.form.controls.postalcode.setValue(data2.postcode ?? null, {emitEvent: false});
                      }
                    },
                    (error): void => {
                      if (error instanceof TimeoutError) {
                        this.searchTaskLocationsTimedOut = true;
                      }
                    });
                }
              },
              (error): void => {
                if (error instanceof TimeoutError) {
                  this.searchTaskLocationsTimedOut = true;
                }
              });
          }
        }

        this.searchTaskLocationsIsLoading = false;
        this.cd.detectChanges();
      }
    );
  }

  clearAddressSuggestions(): void {
    this.searchTaskLocationsTimedOut = false;
    this.searchTaskLocationsIsLoading = false;
  }

  setTime(location: 'created_at_start' | 'created_at_end' | 'closed_at_start' | 'closed_at_end', event: MtxDatetimepickerInputEvent<Date>): void {
    this.form.controls[location].setValue(moment(event.value).toISOString());
  }

  clearValue(field: string): void {
    this.form.get(field).setValue(null);
  }

  close(): void {
    this.form.reset();
    this.form.updateValueAndValidity();
    this.updateFilters.emit(this.form.value);
    this.closed.next();
  }

  closeAndFilter(): void {
    this.updateFilters.emit(this.form.value);
    this.closed.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
